/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useState, useEffect, useMemo, forwardRef, useImperativeHandle} from 'react'
import CurrencyFormat from 'react-currency-format'
import axios from 'axios'
import {useAuth} from '../../modules/auth'
import {Modal, Button} from 'react-bootstrap'
import {ToastContainer, toast} from 'react-toastify'
import {useNavigate} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {useQueryClient} from 'react-query'
// import PreviewPDFModal from './PreviewPDFModal'
import 'react-toastify/dist/ReactToastify.css'
import {Spinner} from 'react-bootstrap'
import {FaUpload, FaTimes} from 'react-icons/fa'
import {IoDocumentTextOutline, IoEyeOutline, IoDownloadOutline} from 'react-icons/io5'
import styled, {keyframes, css} from 'styled-components'
import {FaChevronDown, FaChevronUp} from 'react-icons/fa' // Import icons
import {
  billableRowIndexes,
  BillingWorksheetData,
  seedFromLines,
  summaryFromValidation,
  summaryFromWorksheet,
} from './billing-net-summary'

// Custom number formatting function
export const formatNumberWithSeparator = (value: number, options?: Intl.NumberFormatOptions) => {
  // Floor the value before formatting
  const flooredValue = Math.floor(value * 10000) / 10000

  return new Intl.NumberFormat('id-ID', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 2,
    ...options,
  }).format(flooredValue)
}

// Add interfaces for validated billing data
interface ValidatedBilling {
  subBenefitId: number
  total: number
  paid: number
  excess: number
  remark?: string
}

type UploadAreaProps = React.HTMLAttributes<HTMLDivElement> & {
  $disabled?: boolean
}

type ProgressBarProps = React.HTMLAttributes<HTMLDivElement> & {
  $progress: number
}

const Tooltip = styled.div`
  position: relative;
  display: inline-block;

  &:hover .tooltip-text {
    visibility: visible;
    opacity: 1;
  }
`
const UploadArea = styled.div<UploadAreaProps>`
  ${(props) => css`
    border: 2px dashed ${props.$disabled ? '#ccc' : '#059BB6'};
    border-radius: 8px;
    padding: 24px;
    text-align: center;
    cursor: ${props.$disabled ? 'not-allowed' : 'pointer'};
    transition: all 0.3s ease;
    opacity: ${props.$disabled ? 0.7 : 1};

    &:hover {
      background: ${props.$disabled ? 'none' : 'rgba(5, 155, 182, 0.05)'};
    }
  `}
`

const TooltipText = styled.div`
  visibility: hidden;
  opacity: 0;
  background-color: #2c3e50;
  color: white;
  text-align: center;
  padding: 8px 12px;
  border-radius: 4px;
  position: absolute;
  z-index: 1;
  bottom: 125%;
  left: 50%;
  transform: translateX(-50%);
  transition: opacity 0.3s ease;
  white-space: nowrap;
  font-size: 0.875em;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: #2c3e50 transparent transparent transparent;
  }
`

const TooltipTextRemark = styled.div`
  visibility: hidden;
  opacity: 0;
  background-color: #2c3e50;
  color: white;
  text-align: center;
  padding: 8px 12px;
  border-radius: 4px;
  position: absolute;
  z-index: 99999;
  bottom: 125%;
  left: 20%;
  transform: translateX(-50%);
  transition: opacity 0.3s ease;
  white-space: nowrap;
  font-size: 0.875em;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: #2c3e50 transparent transparent transparent;
  }
`

const StyledLabel = styled.label`
  font-weight: 500;
  color: #2c3e50;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    color: #059bb6;
  }
`

const ProgressBar = styled.div<ProgressBarProps>`
  ${(props) => css`
    width: ${props.$progress}%;
    height: 8px;
    background: linear-gradient(90deg, #059bb6 0%, #05b6a6 100%);
    border-radius: 4px;
    transition: width 0.3s ease;
  `}
`

interface OutpatientPageProps {
  claimDetails?: any
  onSubmit: (e: React.FormEvent, type: 'save' | 'submit') => void
  formData?: any
  onFormDataChange?: (data: any) => void
  setShowConfirmation?: (show: boolean) => void
  isValidateBilling?: boolean
  handleValidateBilling?: (isValidateBilling: boolean) => void
  setIsSaveClaim?: (isSaveClaim: boolean) => void
}

interface BenefitDetail {
  id: number
  detailText: string
}

interface BenefitCondition {
  id: number
  amount: number
  type: string
  conditionType: string
}

interface SubBenefit {
  id: number
  category: string
  subBenefitName: string
  clientsSubBenefit: string
  isCashless: boolean
  isReimburstement: boolean
  isAsCharge: boolean
  annualLimit: number
  type: string
  details: BenefitDetail[]
  condition: BenefitCondition[]
}

interface Benefit {
  id: number
  benefitCode: string
  benefitName: string
  planName: string
  planCode: string
  condition: string
  annualLimit: number
  tnc: string
  tncCategory: string
  subBenefits: SubBenefit[]
}

interface ChangeBenefitRequest {
  claimNumber: string
  updatedBy: string
  benefit: Benefit
}

const OutpatientPage = forwardRef((props: OutpatientPageProps, ref) => {
  const {
    claimDetails,
    onSubmit,
    formData,
    onFormDataChange,
    setShowConfirmation,
    isValidateBilling,
    handleValidateBilling,
    setIsSaveClaim,
  } = props
  const user = useAuth()
  const {auth} = useAuth()
  const {formatMessage: f} = useIntl()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const jsonString = claimDetails?.claim?.detailBenefit
  const parsedJson = jsonString ? JSON.parse(jsonString) : {}
  const parsedMemberDetail = claimDetails?.claim?.memberDetail
    ? JSON.parse(claimDetails?.claim?.memberDetail)
    : {}
  const [diagnosis, setDiagnosis] = useState(formData?.diagnosis || '')
  const [icdData, setIcdData] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedIcd, setSelectedIcd] = useState<{
    code: string
    name: string
  } | null>(null)
  const [supportingDiagnosis, setSupportingDiagnosis] = useState<string[]>(
    formData?.supportingDiagnosis || Array(3).fill('')
  )
  const [supportingIcdData, setSupportingIcdData] = useState<any[][]>(Array(3).fill([]))
  const [selectedSupportingIcds, setSelectedSupportingIcds] = useState<
    ({code: string; name: string} | null)[]
  >(formData?.selectedSupportingIcds || Array(3).fill(null))
  const [doctor, setDoctor] = useState(formData?.doctor || '')
  const [prescription, setPrescription] = useState(formData?.prescription || '')
  const [department, setDepartment] = useState(formData?.department || '')
  const [notes, setNotes] = useState(formData?.notes || '')
  const [specialTerms, setSpecialTerms] = useState(formData?.specialTerms || '')
  // Kept because the parent's form snapshot carries it. The Excess and Cover equivalents are gone:
  // those figures are read-only and now come from the billing summary, not from local state.
  const [totalBiaya, setTotalBiaya] = useState(0)
  const [fglDocumentUrl, setFglDocumentUrl] = useState<string | null>(null)
  const [isLoadingPreview, setIsLoadingPreview] = useState<boolean>(false)
  const [errors, setErrors] = useState({
    diagnosis: false,
    doctor: false,
    department: false,
    // notes: false,
  })

  const [documentsData, setDocumentsData] = useState<any[]>([])
  const [deletingFiles, setDeletingFiles] = useState<Record<string, boolean>>({})
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [files, setFiles] = useState<File[]>([])
  const [uploadProgress, setUploadProgress] = useState<number>(0)
  const [successMessage, setSuccessMessage] = useState<string>('')

  const [showPreview, setShowPreview] = useState(false)
  const [dischargeBillData, setDischargeBillData] = useState<any[]>([])

  const claimData = claimDetails?.claim?.claimData ? JSON.parse(claimDetails.claim.claimData) : null
  const isReadOnly = !['Pre-Registered', 'Registered'].includes(claimDetails?.claim?.status || '')
  const [costs, setCosts] = useState<Record<string, number>>(formData?.costs || {})
  const [excess, setExcess] = useState<Record<string, number>>(formData?.excess || {})
  const [coShare, setCoShare] = useState<Record<string, number>>({})
  const [newExcess, setNewExcess] = useState<Record<string, number>>({})
  const [newTotal, setNewTotal] = useState<Record<string, number>>({})
  const [newRemark, setNewRemark] = useState<Record<string, string>>({})
  const [validateBillingData, setValidateBillingData] = useState<any>()

  // The persisted billing state, from the dedicated provider read. Every figure in the summary
  // comes from here or from a Check Billing response — never from arithmetic on this side.
  // null while unknown (not loaded yet, or the read failed): the net figures are then withheld
  // rather than falling back to gross, which would understate what the member owes.
  const [billingWorksheet, setBillingWorksheet] = useState<BillingWorksheetData | null>(null)
  const [billingWorksheetFailed, setBillingWorksheetFailed] = useState(false)

  // Add new state for validated billing data
  const [validatedBilling, setValidatedBilling] = useState<ValidatedBilling[]>([])
  const [isValidatingBilling, setIsValidatingBilling] = useState(false)
  const [isBtnCheckBilling, setIsBtnCheckBilling] = useState(true)
  const [isProcessClaim, setIsProcessClaim] = useState(false)

  const [benefit, setBenefit] = useState<any[]>([])

  const [showDocModal, setShowDocModal] = useState(false)
  const [showFglDocModal, setShowFglDocModal] = useState(false)

  const [documentFiles, setDocumentFiles] = useState<Record<string, any[]>>({})

  const [selectedPlan, setSelectedPlan] = useState<string>(
    parsedJson?.PlanCode || parsedJson?.PlanName || ''
  )

  const [isExpanded, setIsExpanded] = useState(false) // State to manage expand/collapse
  const [expandedIndexes, setExpandedIndexes] = useState<number[]>([0]) // State to manage expanded indexes - first item expanded by default
  // Auto-expansion runs once per mount, so a later save never reopens rows the provider collapsed.
  const hasAutoExpandedRows = React.useRef(false)

  const [showChangeBenefitModal, setShowChangeBenefitModal] = useState(false) // State for modal visibility
  const [selectedBenefit, setSelectedBenefit] = useState(parsedJson?.BenefitName || '') // State for selected benefit
  const [disabledInputValue, setDisabledInputValue] = useState('') // State for disabled input value
  const [isChanged, setIsChanged] = useState(false) // State to track if the benefit has been changed

  const [showDocumentPreviewModal, setShowDocumentPreviewModal] = useState(false)
  const [previewDocumentUrl, setPreviewDocumentUrl] = useState('')
  const [previewDocumentName, setPreviewDocumentName] = useState('')
  const [isDocumentLoading, setIsDocumentLoading] = useState(false)
  const [productCode, setProductCode] = useState<string>('')

  // Function to handle opening the modal
  const handleChangeBenefitClick = () => {
    // Clear any active input focus to prevent state update conflicts
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
    setShowChangeBenefitModal(true)
  }

  // Function to handle canceling the changes
  const handleCancelChanges = () => {
    setShowChangeBenefitModal(false)
    setIsChanged(false)
  }

  // Function to toggle the expanded state for a specific index
  const toggleExpand = (index: number) => {
    setExpandedIndexes((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    )
  }

  const fetchDocuments = async () => {
    const claimNumber = claimDetails?.claim?.claimNumber
    // Use the apiEndpoint from claimDetails if provided, otherwise use the default endpoint
    const apiUrl = `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/ListFiles/${claimNumber}?area=Outpatient`
    const headers = {
      Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
    }

    try {
      const response = await axios.get(apiUrl, {headers})
      if (response.data.isSuccess) {
        const apiDocuments = response.data.data.filter((doc: any) => doc.label !== 'Prescription')

        // Check for invalid documents across all documents
        const hasInvalidDocuments = apiDocuments.some(
          (doc: any) =>
            doc.document &&
            doc.document.length > 0 &&
            doc.document.some((file: any) => file.status === 'INVALID')
        )

        // For Daily Monitoring, we don't check against defaultDocuments
        const allDocumentsUploaded =
          apiDocuments.length > 0 &&
          apiDocuments.every((doc: any) => doc.document && doc.document.length > 0)

        console.log('response', response)

        // Use API documents directly without merging with defaultDocuments
        const finalDocuments = apiDocuments.map((doc: any, index: number) => ({
          label: doc.label,
          key: index,
          document: doc.document,
        }))

        // Create updated files object
        const updatedFiles: Record<string, any[]> = {}
        finalDocuments.forEach((doc: any) => {
          if (doc.document) {
            updatedFiles[doc.label] = doc.document.map((file: any) => ({
              name: file.name,
              id: file.id,
              type: file.name ? file.name.split('.').pop() : '',
              status: file.status,
              isArchive: file.isArchive,
            }))
          } else {
            updatedFiles[doc.label] = []
          }
        })

        setDocumentsData(finalDocuments)
        // setDocumentFiles(updatedFiles)

        // Update parent components
        // const allDocumentsValid = !hasInvalidDocuments && allDocumentsUploaded
        // onDocumentsStatusChange(allDocumentsValid)
        // if (onDocumentValidationChange) {
        //   onDocumentValidationChange(!allDocumentsValid)
        // }

        // // Check if all documents have IDLE status
        // const allDocumentsIdle = apiDocuments.every(
        //   (doc: any) =>
        //     doc.document &&
        //     doc.document.length > 0 &&
        //     doc.document.every((file: any) => file.status === 'IDLE' || file.status === 'Request Document')
        // )

        // // If all documents are uploaded and have IDLE status, enable the button
        // if (allDocumentsUploaded && allDocumentsIdle) {
        //   onDocumentsStatusChange(true)
        //   if (onDocumentValidationChange) {
        //     onDocumentValidationChange(false)
        //   }
        // }
      }
    } catch (error) {
      console.error('Error fetching documents:', error)
      toast.error('Failed to fetch documents. Please try again.', {
        position: 'bottom-right',
        autoClose: 3000,
      })
    }
  }

  const fetchMemberDetail = async () => {
    const apiUrl = `${process.env.REACT_APP_API_USER_URL}/api/Data/Provider/GetMemberByCard?ProviderCode=${claimDetails?.claim?.providerCode}&CardNo=${claimDetails?.claim?.cardNumber}&Dob=${parsedMemberDetail?.DateOfBirth}`
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
    }
    try {
      setLoading(true)
      const response = await axios.get(apiUrl, {headers})
      if (response.data.isSuccess) {
        setBenefit(response.data.data.benefits)
        setProductCode(response.data.data.productCode)
      }
    } catch (err) {
      console.error('Error fetching member detail:', err)
    } finally {
      setLoading(false)
    }
  }

  // Handle benefit change
  const handleBenefitChange = (value: string): void => {
    setSelectedBenefit(value)
    // Find the corresponding plan for the selected benefit
    const selectedPlan = benefit.find((plan: any) => plan.benefitName === value)
    if (selectedPlan) {
      setSelectedPlan(selectedPlan.planName)
      setIsChanged(true)
    }
  }

  useEffect(() => {
    fetchDocuments()
    // fetchMemberDetail()
  }, [])

  useEffect(() => {
    if (claimDetails?.claim?.status !== 'FGL Released') {
      fetchMemberDetail()
    }
  }, [claimDetails?.claim?.status !== 'FGL Released'])

  const fetchIcdData = async (searchTerm: string, index: number) => {
    const apiUrl = `${process.env.REACT_APP_API_USER_URL}/api/Exclusion/GetPaginatedMstIcd10s?searchTerm=${searchTerm}&pageNumber=1&pageSize=10&productCode=${productCode}&planCode=${selectedPlan}&benefitCode=${parsedJson.BenefitCode}`
    const headers = {
      Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
    }
    try {
      setLoading(true)
      const response = await axios.get(apiUrl, {headers})
      if (index === -1) {
        setIcdData(response.data.data)
      } else {
        const newSupportingIcdData = [...supportingIcdData]
        newSupportingIcdData[index] = response.data.data
        setSupportingIcdData(newSupportingIcdData)
      }
    } catch (err) {
      setError('Error fetching ICD-10 data')
    } finally {
      setLoading(false)
    }
  }

  const fetchIcdDetails = async (icdCode: string) => {
    if (!icdCode) return null
    const apiUrl = `${process.env.REACT_APP_API_USER_URL}/api/Exclusion/GetPaginatedMstIcd10s?searchTerm=${icdCode}&pageNumber=1&pageSize=10&productCode=${productCode}&planCode=${selectedPlan}&benefitCode=${parsedJson.BenefitCode}`
    const headers = {
      Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
    }
    try {
      const response = await axios.get(apiUrl, {headers})
      const matchingIcd = response.data.data.find((icd: any) => icd.code === icdCode)
      return matchingIcd ? `${matchingIcd.name} (${matchingIcd.code})` : icdCode
    } catch (err) {
      console.error('Error fetching ICD details:', err)
      return icdCode
    }
  }

  useEffect(() => {
    const loadExistingData = async () => {
      if (claimData && !diagnosis) {
        // Only load if claimData exists and diagnosis is empty
        // Load ICD codes
        if (claimData.IcdCode1) {
          const icdDetails = await fetchIcdDetails(claimData.IcdCode1)
          setDiagnosis(icdDetails || '')
        }

        // Set supporting diagnoses directly from claimData
        const supportingDiagnosesData = [
          claimData.IcdCode2 ? `${claimData.IcdDesc2 || ''} (${claimData.IcdCode2})` : '',
          claimData.IcdCode3 ? `${claimData.IcdDesc3 || ''} (${claimData.IcdCode3})` : '',
          claimData.IcdCode4 ? `${claimData.IcdDesc4 || ''} (${claimData.IcdCode4})` : '',
        ]

        // Update supporting diagnoses state
        setSupportingDiagnosis(supportingDiagnosesData)

        // Update selected supporting ICDs state with proper null handling
        const supportingIcds = [
          claimData.IcdCode2 ? {code: claimData.IcdCode2, name: claimData.IcdDesc2 || ''} : null,
          claimData.IcdCode3 ? {code: claimData.IcdCode3, name: claimData.IcdDesc3 || ''} : null,
          claimData.IcdCode4 ? {code: claimData.IcdCode4, name: claimData.IcdDesc4 || ''} : null,
        ] as ({code: string; name: string} | null)[]

        setSelectedSupportingIcds(supportingIcds)

        // Load other form data only if empty
        if (!doctor) setDoctor(claimData.Doctor || '')
        if (!department) setDepartment(claimData.PolyName || '')
        if (!notes) setNotes(claimData.Remark || '')
        if (!prescription) setPrescription(claimData.Prescription || '')
      }
    }

    loadExistingData()
  }, []) // Only depend on claimData changes
  // }, [claimData]) // Only depend on claimData changes

  // Prevent unnecessary updates from form data that could cause flickering
  useEffect(() => {
    // Skip if there's no form data or it's empty
    if (!formData || Object.keys(formData).length === 0) {
      return
    }

    // Check if we're in the middle of user input to prevent flickering
    const userIsTyping = document.activeElement && document.activeElement.tagName === 'INPUT'

    // Don't update fields that are currently being edited
    if (
      userIsTyping &&
      document.activeElement &&
      (document.activeElement.id === 'diagnosis-input' ||
        document.activeElement.id === 'supporting-diagnosis-0' ||
        document.activeElement.id === 'supporting-diagnosis-1' ||
        document.activeElement.id === 'supporting-diagnosis-2')
    ) {
      return
    }

    const shouldUpdate =
      formData.diagnosis !== diagnosis ||
      formData.doctor !== doctor ||
      formData.department !== department ||
      formData.notes !== notes ||
      formData.prescription !== prescription ||
      JSON.stringify(formData.supportingDiagnosis) !== JSON.stringify(supportingDiagnosis)

    if (shouldUpdate) {
      // Batch these updates together to prevent multiple renders
      const updates = () => {
        setDiagnosis(formData.diagnosis || diagnosis)
        setDoctor(formData.doctor || doctor)
        setDepartment(formData.department || department)
        setNotes(formData.notes || notes)
        setPrescription(formData.prescription || prescription)
        setSupportingDiagnosis(formData.supportingDiagnosis || supportingDiagnosis)
        setSelectedSupportingIcds(formData.selectedSupportingIcds || selectedSupportingIcds)
        setCosts(formData.costs || costs)
        setExcess(formData.excess || excess)
        // setTotalBiaya(formData.totalBiaya || totalBiaya)
      }

      // Execute updates in next tick to avoid conflict with ongoing render
      setTimeout(updates, 0)
    }
  }, [formData]) // Only depend on formData changes

  // Add debounce helper function to prevent excessive API calls
  const useDebounce = (func, delay) => {
    const timeoutRef = React.useRef<NodeJS.Timeout | null>(null)

    return (...args) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(() => {
        func(...args)
        timeoutRef.current = null
      }, delay)
    }
  }

  // Debounced fetch function to reduce flickering
  const debouncedFetchIcdData = useDebounce((searchTerm, index) => {
    if (searchTerm.length > 2) {
      fetchIcdData(searchTerm, index)
    } else if (index === -1) {
      setIcdData([])
    } else {
      setSupportingIcdData((prev) => {
        const newData = [...prev]
        newData[index] = []
        return newData
      })
    }
  }, 300)

  const handleDiagnosisChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setDiagnosis(value)
    debouncedFetchIcdData(value, -1)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const value = e.target.value
    setSupportingDiagnosis((prev) => {
      const newDiagnoses = [...prev]
      newDiagnoses[index] = value
      return newDiagnoses
    })
    debouncedFetchIcdData(value, index)
  }

  const handleSelectIcd = (icd: {code: string; name: string}) => {
    // Use batch updates to prevent flickering
    const formattedDiagnosis = `${icd.name} (${icd.code})`

    // Update everything in a single render cycle
    setSelectedIcd(icd)
    setDiagnosis(formattedDiagnosis)
    setIcdData([]) // Clear options after selection
  }

  const handleSelectSupportingIcd = (icd: {code: string; name: string}, index: number) => {
    // Create all updates before setting state to reduce render cycles
    const newSelectedIcds = [...selectedSupportingIcds]
    newSelectedIcds[index] = icd

    const formattedDiagnosis = `${icd.name} (${icd.code})`

    // Update all states at once - React will batch these updates
    setSelectedSupportingIcds(newSelectedIcds)
    setSupportingDiagnosis((prev) => {
      const newDiagnoses = [...prev]
      newDiagnoses[index] = formattedDiagnosis // Use formatted string instead of just code
      return newDiagnoses
    })
    setSupportingIcdData((prev) => {
      const newData = [...prev]
      newData[index] = []
      return newData
    })
  }

  // const calculateTotalBiaya = () => {
  //   const total = Object.values(costs).reduce((acc: number, value: number) => {
  //     return acc + (value || 0)
  //   }, 0)
  //   setTotalBiaya(total || 0)
  // }

  // useEffect(() => {
  //   calculateTotalBiaya()
  // }, [costs])

  const validateFields = () => {
    console.log('Current Values:', {
      diagnosis,
      doctor,
      department,
      //   notes,
    })

    const newErrors = {
      diagnosis: !diagnosis,
      doctor: !doctor,
      department: !department,
      //   notes: !notes,
    }
    setErrors(newErrors)
    return Object.values(newErrors).some(Boolean)
  }

  const handleSubmit = async (e: React.FormEvent, type: 'save' | 'submit') => {
    e.preventDefault()
    if (validateFields()) {
      throw new Error(f({id: 'CLAIM.OUTPATIENT.VALIDATION_ERROR'}))
      return
    }

    // Check if claimValue is 0 && excessValue is not 0 show error toast
    const invalidBenefits1 = Object.keys(costs).filter(
      (benefitName) =>
        costs[benefitName] === 0 && excess[benefitName] !== undefined && excess[benefitName] !== 0
    )

    // Check if at least one benefit has a cost
    const hasAtLeastOneCost = Object.values(costs).some((cost) => cost > 0)
    // Only consider invalid if ALL benefits have zero costs
    const invalidBenefits2 = hasAtLeastOneCost ? [] : Object.keys(costs)

    // Merge both validations - either has cost=0 with excess>0, or all costs are zero
    const invalidBenefits = Array.from(new Set(invalidBenefits1.concat(invalidBenefits2)))

    if (invalidBenefits.length > 0) {
      const benefitsList = invalidBenefits.join(', ')
      setShowConfirmation && setShowConfirmation(false)
      setIsSaveClaim && setIsSaveClaim(false)
      toast.error(
        `${(f({id: 'CLAIM.OUTPATIENT.TOTAL_BILLING_NOT_ZERO'}), benefitsList)}`,
        // `Total biaya tidak boleh 0 untuk: ${benefitsList}. Silahkan isi biaya komponen tersebut.`,
        {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'colored',
        }
      )
      return
    }

    var detailMember = JSON.parse(claimDetails?.claim?.memberDetail)

    setLoading(true)
    setIsProcessClaim(true)
    setShowConfirmation && setShowConfirmation(false)

    // Extract ICD code from the diagnosis string
    const extractIcdCode = (diagnosisString: string) => {
      const match = diagnosisString.match(/\((.*?)\)/)
      return match ? match[1] : null
    }

    const finalData = {
      claimNo: claimDetails?.claim?.claimNumber,
      // Main diagnosis (ICD1)
      icdCode1: selectedIcd?.code || extractIcdCode(diagnosis) || null,
      // Supporting diagnoses (ICD2, ICD3, ICD4)
      icdCode2: selectedSupportingIcds[0]?.code || extractIcdCode(supportingDiagnosis[0]) || null,
      icdCode3: selectedSupportingIcds[1]?.code || extractIcdCode(supportingDiagnosis[1]) || null,
      icdCode4: selectedSupportingIcds[2]?.code || extractIcdCode(supportingDiagnosis[2]) || null,
      doctor,
      polyCode: department,
      polyName: department,
      remark: notes,
      updatedBy: user.currentUser?.email,
      BenefitDetail: {
        id: parsedJson.Id,
        benefitName: parsedJson.BenefitName,
        planName: parsedJson.PlanName,
        benefitCode: parsedJson.BenefitCode,
        condition: parsedJson.Condition,
        annualLimit: parsedJson.AnnualLimit,
        subBenefits: parsedJson.SubBenefits.map((subBenefit: any) => ({
          id: subBenefit.Id,
          category: 'OUTPATIENT',
          SubBenefitName: subBenefit.SubBenefitName,
          isCashless: subBenefit.IsCashless,
          isReimburstement: subBenefit.IsReimburstement,
          annualLimit: subBenefit.Amount,
          type: 'OUTPATIENT',
          claimValue: Number(costs[subBenefit.SubBenefitName]),
          excessValue: Number(excess[subBenefit.SubBenefitName]),
          details: subBenefit.Details,
          condition: subBenefit?.Condition || [],
          coShare: coShare[subBenefit.SubBenefitName] || 0,
        })),
      },
    }

    // Log the ICD codes for verification
    console.log('Submitting ICD codes:', {
      icdCode1: finalData.icdCode1,
      icdCode2: finalData.icdCode2,
      icdCode3: finalData.icdCode3,
      icdCode4: finalData.icdCode4,
    })

    // Remove null or undefined ICD codes
    Object.keys(finalData).forEach((key) => {
      if (key.startsWith('icdCode') && !finalData[key]) {
        delete finalData[key]
      }
    })

    console.log('Final Data to Submit: ', JSON.stringify(finalData, null, 2))

    try {
      const headers = {
        Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
      }

      // if (process.env.REACT_APP_API_PROCESS_CLAIM_URL == undefined) {
      const response = await axios.post(
        `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/Provider/ProcessClaim/OUTPATIENT`,
        finalData,
        {headers}
      )

      if (response.data.isSuccess === true) {
        // Store current form state
        const currentFormState = {
          diagnosis,
          doctor,
          department,
          notes,
          prescription,
          supportingDiagnosis,
          selectedSupportingIcds,
          costs,
          totalBiaya,
        }

        // Update parent component with current form state
        if (onFormDataChange) {
          onFormDataChange(currentFormState)
        }

        // The save has just rewritten the applied deductible and co-share server-side, and this
        // save path returns no rollup — so drop the Check Billing figures and re-read the persisted
        // state, which is now the authority for what was stored.
        setValidateBillingData(undefined)
        await fetchBillingWorksheet()

        if (type === 'save') {
          setIsSaveClaim && setIsSaveClaim(true)
          toast.success('Data berhasil disimpan!', {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'light',
          })
        }

        if (type === 'submit') {
          const statusChangeResponse = await fetch(
            `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/UpdateStatus`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
              },
              body: JSON.stringify({
                claimNumber: claimDetails?.claim?.claimNumber,
                status: 'FGL Released',
                username: user.currentUser?.email,
                source: 'Provider',
                emailProvider: claimDetails?.claim?.createdBy,
                treatmentDate: claimDetails?.claim?.indate,
                djp: doctor,
                treatmentPlan: '',
                costEstimation: '',
                currency: '',
                planPolis: detailMember.PlanName,
                memberName: claimDetails?.claim?.memberName,
                diagnose: diagnosis,
                noPolis: claimDetails?.claim?.policyNumber,
                nameProvider: claimDetails?.claim?.providerName,
                insuranceName: claimDetails?.claim?.customerGroup,
                remarkClaim: notes,
              }),
            }
          )

          if (statusChangeResponse.ok) {
            // Invalidate and refetch claims queries to update the UI
            queryClient.invalidateQueries(['claims'])
            queryClient.invalidateQueries(['myTurnTotal'])
            const dischargeBillUrl = `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/Provider/DischargeOutpatientBill?claimNumber=${claimDetails?.claim?.claimNumber}`
            const dischargeBillResponse = await axios.get(dischargeBillUrl, {
              headers,
            })

            if (dischargeBillResponse) {
              const updatedClaimUrl = `${process.env.REACT_APP_API_CLAIM_URL}/api/Attachment/DownloadFGL/${claimDetails?.claim?.claimNumber}`
              const updatedClaimResponse = await axios.get(updatedClaimUrl, {
                headers,
              })
              setShowConfirmation && setShowConfirmation(false)
              setDischargeBillData(dischargeBillResponse.data)
              setShowPreview(true)
              setFglDocumentUrl(updatedClaimResponse?.data?.url)
              console.log('updatedClaimResponse : ', updatedClaimResponse)

              // Add success toast with callback to close modal
              toast.success('Claim already success created, please download the files', {
                position: 'top-right',
                autoClose: 8000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: 'colored',
                style: {
                  background: '#4CAF50',
                  color: 'white',
                  fontSize: '16px',
                  padding: '16px',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                },
                progressStyle: {
                  background: 'rgba(255,255,255,0.7)',
                },
                onClose: () => {
                  setShowPreview(false)
                  navigate('/claims')
                },
              })
            }
          } else if (statusChangeResponse.status === 500) {
            toast.error(f({id: 'CLAIM.OUTPATIENT.FAILED_TO_UPDATE_STATUS'}), {
              position: 'top-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              theme: 'colored',
            })
          } else {
            toast.error(f({id: 'CLAIM.OUTPATIENT.FAILED_TO_UPDATE_STATUS'}), {
              position: 'top-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              theme: 'colored',
            })
          }
        }
        setLoading(false)
        setIsProcessClaim(false)
        // toast.success(
        //                     'Claim successfully submitted!',
        //                     {
        //                         position: "top-right",
        //                         autoClose: 5000,
        //                         hideProgressBar: false,
        //                         closeOnClick: true,
        //                         pauseOnHover: true,
        //                         draggable: true,
        //                         theme: "light",
        //                     }
        //                 );
      } else {
        // Handle unexpected response
        toast.error(f({id: 'CLAIM.OUTPATIENT.FAILED_TO_UPDATE_STATUS'}), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: 'colored',
        })
      }
      // }
    } catch (error) {
      console.error('Error submitting data:', error)
      // Add error toast
      toast.error(f({id: 'CLAIM.OUTPATIENT.FAILED_TO_PROCESS_CLAIM'}), {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: 'colored',
      })
      setLoading(false)
      setIsProcessClaim(false)
      throw error
    } finally {
      setLoading(false)
      setIsProcessClaim(false)
    }
  }

  useImperativeHandle(ref, () => ({
    handleSubmit,
  }))

  // const uniqueSubBenefits = JSON.parse(claimDetails.claim.detailBenefit).SubBenefits.filter(
  //   (subBenefit, index, self) =>
  //     index === self.findIndex((t) => t.SubBenefitName === subBenefit.SubBenefitName)
  // )

  const claimDataSubBenefits = claimDetails?.claim?.claimData
    ? JSON.parse(claimDetails.claim.claimData)
    : null
  const uniqueSubBenefits = (
    claimDataSubBenefits?.TransBenefit
      ? claimDataSubBenefits.TransBenefit
      : JSON.parse(claimDetails.claim.detailBenefit).SubBenefits
  ).filter(
    (subBenefit, index, self) =>
      index === self.findIndex((t) => t.SubBenefitName === subBenefit.SubBenefitName)
  )

  // Update parent component when form fields change
  useEffect(() => {
    if (onFormDataChange) {
      onFormDataChange({
        diagnosis,
        doctor,
        prescription,
        department,
        notes,
        specialTerms,
        costs,
        totalBiaya,
        supportingDiagnosis,
        selectedSupportingIcds,
      })
    }
  }, [
    diagnosis,
    doctor,
    prescription,
    department,
    notes,
    specialTerms,
    costs,
    totalBiaya,
    supportingDiagnosis,
    selectedSupportingIcds,
  ])

  // Initialise the billing form from the SAVED worksheet lines.
  //
  // Runs on every worksheet read, so switching tabs (which remounts this component) restores the
  // figures instead of blanking them. The per-line seeding is guarded so it never overwrites what
  // the provider is part-way through typing; the totals are not guarded because they are read-only
  // and now come straight from the backend.
  useEffect(() => {
    if (!billingWorksheet) return

    const totals = billingWorksheet.totals
    setTotalBiaya(Number(totals?.totalClaim) || 0)

    // Kept from the previous behaviour: a claim that already has saved figures can be submitted
    // without a fresh Check Billing.
    if ((Number(totals?.totalClaim) || 0) > 0) {
      handleValidateBilling && handleValidateBilling(true)
    }

    const seed = seedFromLines(billingWorksheet.lines)

    // Open every billable row on load, so the provider can review all of them without clicking
    // through each one. Rows with no claim amount stay collapsed. When nothing is billable yet — a
    // freshly registered claim — keep the long-standing default of the first row open, otherwise the
    // provider would have to expand a row before they could enter anything.
    if (!hasAutoExpandedRows.current) {
      const billableRows = billableRowIndexes(
        uniqueSubBenefits.map((subBenefit: any) => subBenefit?.SubBenefitName),
        billingWorksheet.lines
      )
      setExpandedIndexes(billableRows.length > 0 ? billableRows : [0])
      hasAutoExpandedRows.current = true
    }

    // The validated columns and the row co-share always follow the server: none of them is typed by
    // the provider, and this effect only runs on entry and after a save, so it cannot overwrite a
    // figure being edited. Keeping them in step with the summary is the point — a table showing the
    // previous Check Billing beside freshly saved totals is the mismatch this screen must not have.
    setNewTotal(seed.newTotal)
    setNewExcess(seed.newExcess)
    setNewRemark(seed.newRemark)
    setCoShare(seed.coShare)

    // The typed inputs are seeded once, so a part-entered bill survives a tab switch.
    if (Object.keys(costs).length > 0) return

    setCosts(seed.costs)
    setExcess(seed.excess)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingWorksheet])

  // Add useEffect to update total when costs change
  // useEffect(() => {
  //   const totalBiaya = Object.values(costs).reduce((sum, value) => sum + (Number(value) || 0), 0)
  //   const totalExcessValue = Object.values(excess).reduce(
  //     (sum, value) => sum + (Number(value) || 0),
  //     0
  //   )
  //   const totalCoverValue = Object.values(newTotal).reduce(
  //     (sum, value) => sum + (Number(value) || 0),
  //     0
  //   )

  //   setTotalBiaya(totalBiaya)
  //   setTotalExcess(totalExcessValue)
  //   setTotalCover(totalCoverValue)
  // }, [costs, excess, newTotal])

  const handleDeleteFile = async (file: any) => {
    const claimNumber = claimDetails?.claim?.claimNumber
    const apiUrl = `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/Provider/DeleteFile?id=${file.id}&claimNumber=${claimNumber}`

    try {
      setDeletingFiles((prev) => ({...prev, [file.id]: true}))

      const response = await axios.post(
        apiUrl,
        {},
        {
          headers: {
            Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
          },
        }
      )

      if (response) {
        // Refresh the documents list
        const listFilesResponse = await axios.get(
          `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/ListFiles/${claimNumber}?area=Outpatient`,
          {
            headers: {
              Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
            },
          }
        )

        if (listFilesResponse.data.isSuccess) {
          const outpatientDocs = listFilesResponse.data.data.find(
            (doc: any) => doc.label === 'Outpatient'
          )
          setDocumentsData((prev) => {
            const newData = prev.filter((item) => item.label !== 'Outpatient')
            return [...newData, outpatientDocs].filter(Boolean)
          })
        }

        toast.success('File deleted successfully!', {
          position: 'top-right',
          autoClose: 3000,
        })
      } else {
        toast.error('Failed to delete file', {
          position: 'top-right',
          autoClose: 3000,
        })
      }
    } catch (error) {
      console.error('Error deleting file:', error)
      toast.error('Error deleting file', {
        position: 'top-right',
        autoClose: 3000,
      })
    } finally {
      setDeletingFiles((prev) => {
        const newState = {...prev}
        delete newState[file.id]
        return newState
      })
    }
  }

  const uploadFiles = async (filesToUpload: File[]) => {
    if (filesToUpload.length === 0) {
      console.log('No files to upload')
      return
    }

    // Check file size limit (10MB)
    const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB in bytes
    const oversizedFiles = filesToUpload.filter((file) => file.size > MAX_FILE_SIZE)

    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles.map((f) => f.name).join(', ')
      toast.error(`File size exceeds maximum allowed size of 10MB: ${fileNames}`, {
        position: 'bottom-right',
        autoClose: 5000,
      })
      return
    }

    const formData = new FormData()
    filesToUpload.forEach((file) => {
      formData.append('files', file, file.name)
    })

    const claimNumber = claimDetails?.claim?.claimNumber
    console.log('Claim Number : ', claimDetails)
    const apiUrl = `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/UploadFiles`

    const queryParams = new URLSearchParams({
      claimNumber: claimNumber,
      typeDocument: 'Outpatient',
      user: user.currentUser?.email || '',
      status: 'VALID',
      bucket: 'Outpatient',
      id: '0',
    })

    const fullUrl = `${apiUrl}?${queryParams.toString()}`

    setIsUploading(true)
    setUploadProgress(0)
    setSuccessMessage('')

    try {
      console.log('Uploading files with FormData:', formData)
      Array.from(formData.entries()).forEach((pair) => {
        console.log(pair[0], pair[1])
      })

      const response = await axios.post(fullUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
          accept: '*/*',
        },
        onUploadProgress: (progressEvent) => {
          const total = progressEvent.total || 0
          const current = progressEvent.loaded
          const percentage = Math.round((current / total) * 100)
          setUploadProgress(percentage)
        },
      })

      if (response.data.isSuccess) {
        console.log('Files uploaded successfully:', response.data)

        const listFilesResponse = await axios.get(
          `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/ListFiles/${claimNumber}?area=Outpatient`,
          {
            headers: {
              Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
            },
          }
        )

        if (listFilesResponse.data.isSuccess) {
          const outpatientDocs = listFilesResponse.data.data.find(
            (doc: any) => doc.label === 'Outpatient'
          )
          setDocumentsData((prev) => {
            const newData = prev.filter((item) => item.label !== 'Outpatient')
            return [...newData, outpatientDocs].filter(Boolean)
          })
        }

        setSuccessMessage('Files uploaded successfully!')
        setTimeout(() => {
          setSuccessMessage('')
        }, 3000)
      } else {
        console.error('Failed to upload files:', response)
        toast.error('Failed to upload files')
      }
    } catch (error: any) {
      console.error('Error uploading files:', error)
      console.error('Error response:', error.response?.data)
      toast.error(error.response?.data?.message || 'Error uploading files')
    } finally {
      setIsUploading(false)
      setUploadProgress(0)
    }
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files)
      setFiles((prevFiles) => [...prevFiles, ...selectedFiles])
      console.log('Selected files:', selectedFiles)
      await uploadFiles(selectedFiles)
    }
  }

  // Handle benefit and plan change from the BenefitPlanEditor
  const onBenefitChange = (benefit: string, plan: string): void => {
    setSelectedBenefit(benefit)
    setSelectedPlan(plan)
  }

  const changeClaimBenefit = async (data: ChangeBenefitRequest): Promise<any> => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/ChangesBenefit`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
          },
          body: JSON.stringify(data),
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to change claim benefit')
      }

      return await response.json()
    } catch (error) {
      console.error('Error changing claim benefit:', error)
      throw error
    }
  }

  // Save changes
  const handleSaveChangeBenefit = async (): Promise<void> => {
    try {
      setLoading(true)
      // setDisabledButton(true);

      // Get current user information
      const token = user.currentUser?.token || ''
      const email = user.currentUser?.email || ''

      // Find the selected benefit plan object
      const selectedBenefitPlan = benefit?.find((plan: any) => plan.benefitName === selectedBenefit)

      if (!selectedBenefitPlan) {
        throw new Error('Selected benefit plan not found')
      }

      // Prepare the request payload
      const requestData = {
        claimNumber: claimDetails?.claim?.claimNumber,
        updatedBy: email,
        benefit: {
          id: selectedBenefitPlan.id || 0,
          benefitCode: selectedBenefitPlan.benefitCode || '',
          benefitName: selectedBenefitPlan.benefitName || '',
          planName: selectedBenefitPlan.planName || '',
          planCode: selectedBenefitPlan.planCode || '',
          condition: selectedBenefitPlan.condition || '',
          annualLimit: selectedBenefitPlan.annualLimit || 0,
          tnc: selectedBenefitPlan.tnc || '',
          tncCategory: selectedBenefitPlan.tncCategory || '',
          subBenefits: selectedBenefitPlan.subBenefits || [],
        },
      }

      // Call the API to change the benefit
      await changeClaimBenefit(requestData)

      // Notify parent component of changes
      if (onBenefitChange) {
        onBenefitChange(selectedBenefit, selectedPlan)
      }

      setIsChanged(false)
      toast.success('Benefit and plan updated successfully')
      // setViewOnly(true);

      // Reload the page after a short delay to show the toast message
      setTimeout(() => {
        navigate('/claims')
      }, 1500)
    } catch (error: any) {
      console.error('Error saving benefit and plan:', error)
      toast.error(error.message || 'Failed to update benefit and plan')
    } finally {
      setLoading(false)
      setIsChanged(false)
      // setDisabledButton(false);
    }
  }

  console.log('Documents Data : ', documentsData)
  const renderDischargeDocuments = () => {
    const outpatientDocs = documentsData.find((doc) => doc.label === 'Outpatient')?.document || []
    const canAccessDocuments = ['FGL Released', 'Claim Verification'].includes(
      claimDetails?.claim?.status || ''
    )

    return outpatientDocs.map((doc) => (
      <div
        key={doc.id}
        className='file-item'
        style={{
          display: 'flex',
          alignItems: 'center',
          margin: '8px',
          justifyContent: 'space-between',
          backgroundColor: 'white',
          padding: '12px 16px',
          borderRadius: '8px',
          width: 'calc(100% - 16px)',
          boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
          border: '1px solid #f0f0f0',
          transition: 'all 0.2s ease',
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.08)'
          e.currentTarget.style.transform = 'translateY(-2px)'
          e.currentTarget.style.borderColor = '#e0e0e0'
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.04)'
          e.currentTarget.style.transform = 'translateY(0)'
          e.currentTarget.style.borderColor = '#f0f0f0'
        }}
      >
        <div style={{display: 'flex', alignItems: 'center', overflow: 'hidden'}}>
          <div
            style={{
              backgroundColor: '#e9f5f8',
              padding: '10px',
              borderRadius: '8px',
              marginRight: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IoDocumentTextOutline style={{color: '#059BB6'}} />
          </div>
          <div style={{overflow: 'hidden'}}>
            <div
              style={{
                fontWeight: 500,
                color: '#2c3e50',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '200px',
              }}
            >
              {doc.name}
            </div>
            <div
              style={{
                fontSize: '12px',
                color: '#6c757d',
                marginTop: '2px',
              }}
            >
              {new Date().toLocaleDateString()} • {(doc.size || '').toString()}
            </div>
          </div>
        </div>
        <div style={{display: 'flex', gap: '8px'}}>
          {canAccessDocuments && (
            <>
              <button
                onClick={(e) => {
                  e.preventDefault()
                  previewDocument(doc.name)
                }}
                type='button'
                style={{
                  border: 'none',
                  background: '#e3f2fd',
                  padding: '8px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  color: '#0d6efd',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'all 0.2s ease',
                }}
                title='Preview file'
                onMouseOver={(e) => {
                  e.currentTarget.style.background = '#cce5ff'
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = '#e3f2fd'
                }}
              >
                <IoEyeOutline />
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault()
                  downloadDocument(claimDetails?.claim?.claimNumber, doc.name)
                }}
                type='button'
                style={{
                  border: 'none',
                  background: '#e8f5e9',
                  padding: '8px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  color: '#28a745',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'all 0.2s ease',
                }}
                title='Download file'
                onMouseOver={(e) => {
                  e.currentTarget.style.background = '#c8e6c9'
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = '#e8f5e9'
                }}
              >
                <IoDownloadOutline />
              </button>
            </>
          )}
          {!isReadOnly && (
            <button
              onClick={() => handleDeleteFile(doc)}
              disabled={deletingFiles[doc.id]}
              style={{
                border: 'none',
                background: deletingFiles[doc.id] ? '#f8d7da' : '#fff4f4',
                padding: '8px',
                borderRadius: '6px',
                cursor: 'pointer',
                color: '#dc3545',
                display: 'flex',
                alignItems: 'center',
                transition: 'all 0.2s ease',
              }}
              title='Delete file'
              onMouseOver={(e) => {
                if (!deletingFiles[doc.id]) {
                  e.currentTarget.style.background = '#f8d7da'
                }
              }}
              onMouseOut={(e) => {
                if (!deletingFiles[doc.id]) {
                  e.currentTarget.style.background = '#fff4f4'
                }
              }}
            >
              {deletingFiles[doc.id] ? (
                <span style={{fontSize: '12px', fontWeight: 500}}>Deleting...</span>
              ) : (
                <FaTimes />
              )}
            </button>
          )}
        </div>
      </div>
    ))
  }

  // Reads the SAVED billing state: config snapshot, applied plan adjustments, rollup-vocabulary
  // totals and the worksheet lines. Used on entry and again after a save.
  //
  // The claim service's general rule is to refresh from a save response's rollup, but the provider
  // save path (Provider/ProcessClaim/OUTPATIENT) returns no rollup, so re-reading is the sanctioned
  // refresh for this flow. The claim JSON carried in router state is a Pub/Sub replica and is
  // deliberately not a source for money figures.
  const fetchBillingWorksheet = async () => {
    const claimNumber = claimDetails?.claim?.claimNumber
    if (!claimNumber) return

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/Provider/BillingWorksheet/${claimNumber}`,
        {
          headers: {
            Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
          },
        }
      )

      // Claim-not-found comes back as HTTP 200 with isSuccess: false — never branch on status alone.
      if (response.data?.isSuccess && response.data?.data) {
        setBillingWorksheet(response.data.data)
        setBillingWorksheetFailed(false)
      } else {
        setBillingWorksheet(null)
        setBillingWorksheetFailed(true)
      }
    } catch (error) {
      console.error('Error fetching billing worksheet:', error)
      setBillingWorksheet(null)
      setBillingWorksheetFailed(true)
    }
  }

  useEffect(() => {
    fetchBillingWorksheet()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimDetails?.claim?.claimNumber])

  // One source at a time, so the figures on screen always describe a single coherent state: the
  // Check Billing rollup while it is fresh (a line edit discards it), otherwise the persisted read.
  const billingSummary = useMemo(
    () =>
      validateBillingData
        ? summaryFromValidation(validateBillingData)
        : summaryFromWorksheet(billingWorksheet),
    [validateBillingData, billingWorksheet]
  )

  // Renders a money figure, or an em dash when the figure is unknown. Withholding beats guessing:
  // providers read Total Excess to decide what to collect from the patient.
  const showAmount = (value?: number | null) =>
    typeof value === 'number'
      ? `Rp ${formatNumberWithSeparator(value, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })}`
      : '—'

  // Add validateBilling function
  const validateBilling = async () => {
    // Clear any active input focus to prevent state update conflicts
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }

    const claimNumber = claimDetails?.claim?.claimNumber
    const cardNumber = claimDetails?.claim?.cardNumber

    if (!claimNumber || !cardNumber) {
      toast.error('Claim number or card number is missing')
      return
    }

    try {
      setIsValidatingBilling(true)
      setLoading(true)

      const apiUrl = `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/ValidateOutpatientWorksheet`

      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
      }

      // Filter out subBenefits with empty or zero costs
      const payload = uniqueSubBenefits
        .filter((subBenefit: any) => {
          const cost = costs[subBenefit.SubBenefitName] || 0
          return cost > 0 // Only include items with costs greater than zero
        })
        .map((subBenefit: any) => ({
          idBenefit: subBenefit.Id,
          claimNumber,
          cardNumber,
          // claimValue: costs[subBenefit.SubBenefitName] || 0,
          claimAmount: costs[subBenefit.SubBenefitName] || 0,
          exclusionAmount: excess[subBenefit.SubBenefitName] || 0,
          prorate: false,
          qty: 1,
          roomBenefit: false,
          coShare: coShare[subBenefit.SubBenefitName] || 0,
        }))

      // Structure payload with claimNumber and updatedBy at top level
      // const payload = {
      //  worksheetItems
      // };

      console.log('Validation payload:', payload)

      const response = await axios.post(apiUrl, payload, {headers})

      if (response.data.isSuccess) {
        // Update validated billing state with response data
        const validatedResults = response.data.data.map((item: any) => ({
          subBenefitId: item.idBenefit,
          total: item.claimValue || item.claimAmount || 0,
          paid: item.paid || 0,
          excess: item.excess || 0,
          remark: item.remark || '',
        }))

        setValidatedBilling(validatedResults)

        // Update excess state with validated values
        const newExcess: Record<string, number> = {}
        const newTotalValues: Record<string, number> = {}
        const newRemarkValues: Record<string, string> = {}

        response.data.data.forEach((item: any) => {
          const subBenefit = uniqueSubBenefits.find((sb: any) => sb.Id === item.idBenefit)
          if (subBenefit) {
            newExcess[subBenefit.SubBenefitName] = item.excess || 0
            newTotalValues[subBenefit.SubBenefitName] = item.paid || 0
            newRemarkValues[subBenefit.SubBenefitName] = item.remark || ''
          }
        })

        setNewExcess(newExcess)
        setNewTotal(newTotalValues)
        setNewRemark(newRemarkValues)
        setValidateBillingData(response.data)
        handleValidateBilling && handleValidateBilling(response.data.isValidate)

        toast.success('Checking Billing successful')
      } else {
        toast.error(response.data.message || 'Validation failed')
      }
    } catch (error: any) {
      console.error('Error validating billing:', error)
      toast.error(error.response?.data?.message || 'Error validating billing')
      handleValidateBilling && handleValidateBilling(false)
    } finally {
      setIsValidatingBilling(false)
      setLoading(false)
    }
  }

  // Add the following useEffect after all other useEffects
  useEffect(() => {
    // When fglDocumentUrl is set but we're still loading,
    // create a hidden iframe to load the document
    if (fglDocumentUrl && isLoadingPreview) {
      // The actual change happens when the iframe loads in the modal
      console.log('FGL document URL is set, loading document...')
    }
  }, [fglDocumentUrl, isLoadingPreview])

  // Uncomment and modify the downloadDocument function
  const downloadDocument = async (claimNumber: string, fileName: string) => {
    try {
      const url = `${process.env.REACT_APP_API_CLAIM_URL}/api/Attachment/download-url/${claimNumber}/${fileName}`
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
        },
      })
      const responseData = await response.data
      if (!response.data.isSuccess) {
        console.error(`HTTP error! status: ${response.status}`)
        console.error('Error response:', responseData)
        throw new Error(responseData.title ?? `HTTP error! status: ${response.status}`)
      }

      // Open download URL in new tab or initiate download
      window.open(responseData.data.download_url, '_blank')
      return responseData
    } catch (error) {
      console.error('Error downloading document:', error)
      toast.error('Failed to download document. Please try again.', {
        position: 'bottom-right',
        autoClose: 3000,
      })
      throw error
    }
  }

  // Add a preview document function
  const previewDocument = async (fileName: string) => {
    const claimNumber = claimDetails?.claim?.claimNumber
    try {
      setIsDocumentLoading(true)
      setShowDocumentPreviewModal(true)

      const url = `${process.env.REACT_APP_API_CLAIM_URL}/api/Attachment/download-url/${claimNumber}/${fileName}`
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
        },
      })
      const responseData = await response.data
      console.log('Response data:', responseData)

      // Set the preview URL and open modal
      setPreviewDocumentUrl(responseData.data.download_url)
      setPreviewDocumentName(fileName)
    } catch (error) {
      console.error('Error previewing document:', error)
      toast.error('Failed to preview document. Please try again.', {
        position: 'bottom-right',
        autoClose: 3000,
      })
      setIsDocumentLoading(false)
    }
  }

  return (
    <>
      <ToastContainer
        position='top-right'
        autoClose={8000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme='colored'
        style={{zIndex: 9999}} // Ensure toast appears above modal
      />
      {(loading || isValidatingBilling || isProcessClaim) && (
        <div
          className='loading-indicator'
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 1000,
          }}
        >
          <Spinner animation='border' />
          <span style={{marginLeft: '10px'}}>
            {isValidatingBilling
              ? f({id: 'CLAIM.OUTPATIENT.CHECKING_BILLING'})
              : isProcessClaim
              ? f({id: 'CLAIM.OUTPATIENT.PROCESSING_CLAIM'})
              : f({id: 'CLAIM.OUTPATIENT.LOADING'})}
          </span>
        </div>
      )}
      <div
        className='container'
        style={{
          // width: '1280px',
          height: '1014px',
          display: 'flex',
          gap: '24px',
          fontFamily: 'Inter, sans-serif',
          fontSize: '14px',
          justifyContent: 'space-between',
          padding: '0px',
          // flexDirection: 'row',
          // flexWrap: 'wrap',
        }}
      >
        {/* Left Column */}
        <div
          className='bg-white p-6 rounded shadow-md'
          style={{
            // width: '816px',
            width: '50%',
            // height: '820px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <h3 className='text-lg font-semibold mb-4'>{f({id: 'CLAIM.OUTPATIENT.FORM_TITLE'})}</h3>
          <form style={{overflowY: 'auto'}}>
            <div className='mb-4'>
              <label className='form-label'>
                {f({id: 'CLAIM.OUTPATIENT.DIAGNOSIS_MAIN'})}{' '}
                <span style={{color: '#059BB6'}}> *</span>
              </label>
              <div style={{position: 'relative', width: '100%'}}>
                <input
                  id='diagnosis-input'
                  type='text'
                  className={`form-control ${errors.diagnosis ? 'border-red-500' : ''}`}
                  value={diagnosis}
                  onChange={(e) => {
                    !isReadOnly && handleDiagnosisChange(e)
                    setErrors({...errors, diagnosis: false})
                  }}
                  placeholder={f({id: 'CLAIM.OUTPATIENT.DIAGNOSIS_MAIN_PLACEHOLDER'})}
                  required
                  readOnly={isReadOnly}
                  style={isReadOnly ? {backgroundColor: '#f5f5f5'} : {}}
                  autoComplete='off' // Prevent browser autocomplete
                />
                {icdData.length > 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      zIndex: 9999,
                      background: 'white',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      marginTop: '2px',
                    }}
                  >
                    {icdData.map((icd) => (
                      <div
                        key={icd.id}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #eee',
                        }}
                        onClick={() => handleSelectIcd({code: icd.code, name: icd.name})}
                      >
                        {icd.name} ({icd.code})
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className='mb-4'>
              <label className='form-label'>
                {f({id: 'CLAIM.OUTPATIENT.DIAGNOSIS_SUPPORTING'})}
              </label>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '16px',
                  flexWrap: 'wrap',
                }}
              >
                {[2, 3, 4].map((diagNumber, arrayIndex) => (
                  <div
                    key={diagNumber}
                    style={{
                      position: 'relative',
                      flex: '1',
                      minWidth: '200px',
                      // maxWidth: '300px',
                      maxWidth: '282px',
                    }}
                  >
                    <input
                      id={`supporting-diagnosis-${arrayIndex}`}
                      type='text'
                      className={`form-control ${error ? 'border-red-500' : ''}`}
                      value={supportingDiagnosis[arrayIndex]}
                      onChange={(e) => !isReadOnly && handleInputChange(e, arrayIndex)}
                      placeholder={`${f({id: 'CLAIM.OUTPATIENT.ICD10_KEY'})} ${diagNumber}`}
                      readOnly={isReadOnly}
                      style={isReadOnly ? {backgroundColor: '#f5f5f5'} : {}}
                      autoComplete='off' // Prevent browser autocomplete
                    />
                    {supportingIcdData[arrayIndex]?.length > 0 && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          zIndex: 9999,
                          background: 'white',
                          border: '1px solid #ced4da',
                          borderRadius: '4px',
                          boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          marginTop: '2px',
                        }}
                      >
                        {supportingIcdData[arrayIndex].map((icd, index) => (
                          <div
                            key={icd.code || index}
                            style={{
                              padding: '8px 12px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #eee',
                            }}
                            onClick={() =>
                              handleSelectSupportingIcd(
                                {code: icd.code, name: icd.name},
                                arrayIndex
                              )
                            }
                          >
                            {icd.name} ({icd.code})
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className='mb-4'>
              <label className='form-label'>
                {f({id: 'CLAIM.OUTPATIENT.DOCTOR'})} <span style={{color: '#059BB6'}}> *</span>
              </label>
              <input
                type='text'
                className={`form-control ${errors.doctor ? 'border-red-500' : ''}`}
                value={doctor}
                onChange={(e) => {
                  !isReadOnly && setDoctor(e.target.value)
                  setErrors({...errors, doctor: false})
                }}
                readOnly={isReadOnly}
                style={isReadOnly ? {backgroundColor: '#f5f5f5'} : {}}
                required
              />
            </div>
            {/* <div className="mb-4">
                            <label className="form-label">Resep</label>
                            <input
                                type="text"
                                className="form-control"
                                value={prescription}
                                onChange={(e) => !isReadOnly && setPrescription(e.target.value)}
                                readOnly={isReadOnly}
                                style={isReadOnly ? { backgroundColor: '#f5f5f5' } : {}}
                            />
                        </div> */}
            <div className='mb-4'>
              <label className='form-label'>
                {f({id: 'CLAIM.OUTPATIENT.POLI'})} <span style={{color: '#059BB6'}}> *</span>
              </label>
              <input
                type='text'
                className={`form-control ${errors.department ? 'border-red-500' : ''}`}
                value={department}
                onChange={(e) => {
                  !isReadOnly && setDepartment(e.target.value)
                  setErrors({...errors, department: false})
                }}
                readOnly={isReadOnly}
                style={isReadOnly ? {backgroundColor: '#f5f5f5'} : {}}
                required
              />
            </div>

            {/* {Upload doc} */}
            <div className='mb-4'>
              <Tooltip>
                <StyledLabel>
                  {/* <FaPrescription /> */}
                  {f({id: 'CLAIM.OUTPATIENT.UPLOAD_DOCUMENT'})}
                </StyledLabel>
                <TooltipText className='tooltip-text'>
                  {f({id: 'CLAIM.OUTPATIENT.UPLOAD_DOCUMENT_TOOLTIP'})}
                </TooltipText>
              </Tooltip>
              <UploadArea
                $disabled={isReadOnly}
                style={{
                  background: !isReadOnly ? 'linear-gradient(145deg, #f8fbfc, #e9f5f8)' : '#f5f5f5',
                  boxShadow: !isReadOnly ? '0 4px 15px rgba(5, 155, 182, 0.08)' : 'none',
                  transition: 'all 0.3s ease',
                  padding: '12px 16px',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '12px',
                  borderWidth: '2px',
                  borderStyle: 'dashed',
                  borderColor: !isReadOnly ? '#059BB6' : '#ccc',
                  height: '40px',
                }}
              >
                <input
                  type='file'
                  multiple
                  onChange={handleFileChange}
                  style={{display: 'none'}}
                  id='file-upload'
                  disabled={isReadOnly}
                />
                <label
                  htmlFor='file-upload'
                  style={{
                    cursor: !isReadOnly ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    width: '100%',
                    margin: 0,
                  }}
                >
                  <div
                    style={{
                      background: !isReadOnly ? 'rgba(5, 155, 182, 0.1)' : '#e9e9e9',
                      borderRadius: '50%',
                      width: '28px',
                      height: '28px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '12px',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <FaUpload
                      style={{
                        fontSize: '14px',
                        color: !isReadOnly ? '#059BB6' : '#6c757d',
                      }}
                    />
                  </div>
                  <div>
                    <p
                      style={{
                        margin: 0,
                        color: !isReadOnly ? '#2c3e50' : '#6c757d',
                        fontSize: '14px',
                        fontWeight: 500,
                      }}
                    >
                      {!isReadOnly ? 'Upload Dokumen' : 'File upload disabled'}
                    </p>
                  </div>
                </label>
              </UploadArea>

              {isUploading && (
                <div
                  style={{
                    marginTop: '16px',
                    background: 'white',
                    padding: '16px',
                    borderRadius: '10px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    border: '1px solid #e9ecef',
                  }}
                >
                  <div
                    style={{
                      marginBottom: '10px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        color: '#059BB6',
                        fontWeight: 500,
                      }}
                    >
                      <Spinner
                        animation='border'
                        size='sm'
                        style={{
                          marginRight: '8px',
                          color: '#059BB6',
                          width: '16px',
                          height: '16px',
                        }}
                      />
                      Uploading files...
                    </span>
                    <span
                      style={{
                        background: '#059BB6',
                        color: 'white',
                        borderRadius: '20px',
                        padding: '2px 10px',
                        fontSize: '14px',
                        fontWeight: 500,
                      }}
                    >
                      {uploadProgress}%
                    </span>
                  </div>
                  <div
                    style={{
                      background: '#f0f0f0',
                      borderRadius: '6px',
                      height: '8px',
                      overflow: 'hidden',
                    }}
                  >
                    <ProgressBar $progress={uploadProgress} />
                  </div>
                </div>
              )}

              {documentsData.find((doc) => doc.label === 'Outpatient')?.document?.length > 0 && (
                <div
                  style={{
                    marginTop: '16px',
                    background: 'white',
                    borderRadius: '10px',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                    overflow: 'hidden',
                    border: '1px solid #e9ecef',
                    maxHeight: '250px',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <div
                    style={{
                      padding: '12px 16px',
                      borderBottom: '1px solid #f0f0f0',
                      display: 'flex',
                      alignItems: 'center',
                      background: '#f8f9fa',
                      flex: '0 0 auto',
                    }}
                  >
                    <IoDocumentTextOutline style={{color: '#059BB6', marginRight: '8px'}} />
                    <span style={{fontWeight: 500, color: '#2c3e50'}}>Uploaded Files</span>
                  </div>
                  <div
                    style={{
                      padding: '8px',
                      overflowY: 'auto',
                      maxHeight: 'calc(250px - 45px)',
                      flex: '1 1 auto',
                    }}
                  >
                    {renderDischargeDocuments()}
                  </div>
                </div>
              )}

              {successMessage && (
                <div
                  style={{
                    marginTop: '16px',
                    padding: '12px 16px',
                    background: '#d4edda',
                    color: '#155724',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 500,
                    boxShadow: '0 2px 6px rgba(21, 87, 36, 0.1)',
                  }}
                >
                  {successMessage}
                </div>
              )}
            </div>

            {/* //handling Catatan & Ketentuan Khusus tidak required optional */}
            <div className='mb-4'>
              <label className='form-label'>
                {f({id: 'CLAIM.OUTPATIENT.NOTES'})}
                {/* <span style={{ color: '#059BB6' }}> *</span> */}
              </label>
              <textarea
                // className={`form-control ${errors.notes ? 'border-red-500' : ''}`}
                className={`form-control`}
                value={notes}
                onChange={(e) => !isReadOnly && setNotes(e.target.value)}
                readOnly={isReadOnly}
                style={isReadOnly ? {backgroundColor: '#f5f5f5'} : {}}
                required
              />
            </div>
            {/* <div className='mb-4'>
              <label className='form-label'>
                Ketentuan Khusus
              </label>
              <textarea
                className='form-control'
                value={specialTerms}
                onChange={(e) => setSpecialTerms(e.target.value)}
                readOnly={isReadOnly}
                placeholder={(() => {
                  try {
                    // Parse claimData first since it's a JSON string
                    const claimData = claimDetails?.claim?.claimData
                    return claimDetails?.claim?.detailBenefit?.Condition
                      ? JSON.parse(claimDetails.claim.detailBenefit.Condition)
                      : ''
                  } catch (error) {
                    console.error('Error parsing data:', error)
                    return ''
                  }
                })()}
                disabled
              />
            </div> */}
            <div className='mb-4'>
              <label className='form-label'>{f({id: 'CLAIM.OUTPATIENT.SPECIAL_TERMS'})}</label>
              <div
                dangerouslySetInnerHTML={{
                  __html: parsedJson.Condition || '',
                }}
                style={{
                  backgroundColor: '#f8f9fa',
                  color: '#6c757d',
                  cursor: 'not-allowed',
                  padding: '12px',
                  minHeight: '120px',
                  border: '2px solid #e9ecef',
                  borderRadius: '8px',
                  overflowY: 'auto',
                }}
              />
            </div>
            {claimDetails?.claim?.status === 'Void' && claimDetails?.claim?.remarkRejectVoid && (
              <div className='mb-4'>
                <label className='form-label'>
                  {f({id: 'CLAIM.OUTPATIENT.REMARK_REJECT_VOID'})}
                </label>
                <textarea
                  className='form-control'
                  value={claimDetails?.claim?.remarkRejectVoid}
                  readOnly={isReadOnly}
                  disabled
                />
              </div>
            )}
            {claimDetails?.urlRegisterForm && (
              <div className='info-item full-width card-layout'>
                <div className='info-label' style={{marginBottom: '5px'}}>
                  <label>{f({id: 'CLAIM.OUTPATIENT.REGISTRATION_DOCUMENT'})}</label>
                </div>
                <div className='info-value'>
                  <div
                    className='card-content'
                    style={{
                      padding: '16px',
                      border: '1px solid #dbdfe9',
                      borderRadius: '8px',
                      backgroundColor: '#f1f1f2',
                      cursor: 'pointer',
                      color: '#059BB6',
                      textDecoration: 'none',
                    }}
                    onClick={() => setShowDocModal(true)}
                  >
                    {f({id: 'CLAIM.OUTPATIENT.VIEW_REGISTRATION_DOCUMENT'})}
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Right Column: Cost Section */}
        <div
          className='bg-white p-6 rounded shadow-md'
          style={{
            // width: '440px',
            width: '50%',
            height: '1023px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          {/* Fixed Total Biaya section */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '16px',
              alignItems: 'center',
            }}
          >
            <h2 className='text-xl font-bold mb-4'>{f({id: 'CLAIM.OUTPATIENT.ESTIMATE_COST'})}</h2>
            {claimDetails?.claim?.status === 'Pre-Registered' && (
              <>
                <div className='flex flex-row gap-2' style={{display: 'flex', gap: '16px'}}>
                  <button
                    style={{
                      backgroundColor: '#059BB6',
                      // border: '1px solid #059BB6',
                      color: 'white',
                      padding: '10px 20px',
                      borderRadius: '5px',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                    onClick={handleChangeBenefitClick}
                  >
                    {f({id: 'CLAIM.OUTPATIENT.CHANGE_BENEFIT'})}
                  </button>
                  <button
                    style={{
                      backgroundColor:
                        isValidatingBilling || isReadOnly || isBtnCheckBilling
                          ? '#cccccc'
                          : '#059BB6',
                      color: 'white',
                      padding: '10px 20px',
                      borderRadius: '5px',
                      border: 'none',
                      cursor:
                        isValidatingBilling || isReadOnly || isBtnCheckBilling
                          ? 'not-allowed'
                          : 'pointer',
                    }}
                    onClick={validateBilling}
                    disabled={isValidatingBilling || isReadOnly || isBtnCheckBilling}
                  >
                    {f({id: 'CLAIM.OUTPATIENT.CHECK_BILLING'})}
                  </button>
                </div>
              </>
            )}
          </div>

          <div style={{display: 'flex', justifyContent: 'space-between'}}>
            <div
              style={{
                backgroundColor: '#059BB6',
                // padding: '16px',
                padding: '8px',
                borderRadius: '8px',
                color: 'white',
                textAlign: 'left',
                flex: 1, // Allow equal width for each section
                marginRight: '8px', // Add some space between sections
                height: '66px',
              }}
            >
              <h4
                className='font-bold mb-0'
                style={{
                  color: 'white',
                  marginBottom: '0px !important',
                  fontSize: '16px',
                }}
              >
                {f({id: 'CLAIM.OUTPATIENT.TOTAL_BILLING'})}
              </h4>
              {/* <p className='text-3xl font-bold mb-4' style={{color: 'white'}}>Rp {totalBiaya.toLocaleString()}</p> */}
              <p className='text-3xl font-bold mb-4' style={{color: 'white'}}>
                {showAmount(billingSummary?.totalBilling)}
              </p>
            </div>

            <div
              style={{
                backgroundColor: '#059BB6',
                // padding: '16px',
                padding: '8px',
                borderRadius: '8px',
                color: 'white',
                textAlign: 'left',
                flex: 1, // Allow equal width for each section
                marginRight: '8px', // Add some space between sections
                height: '66px',
              }}
            >
              <h4
                className='font-bold mb-0'
                style={{
                  color: 'white',
                  marginBottom: '0px !important',
                  fontSize: '16px',
                }}
              >
                {f({id: 'CLAIM.OUTPATIENT.TOTAL_EXCESS'})}
              </h4>
              {/* <p className='text-3xl font-bold mb-4' style={{color: 'white'}}>Rp {totalBiaya.toLocaleString()}</p> */}
              <p className='text-3xl font-bold mb-4' style={{color: 'white'}}>
                {showAmount(billingSummary?.totalExcessNet)}
              </p>
            </div>

            <div
              style={{
                backgroundColor: '#059BB6',
                // padding: '16px',
                padding: '8px',
                borderRadius: '8px',
                color: 'white',
                textAlign: 'left',
                flex: 1, // Allow equal width for each section
                height: '66px',
              }}
            >
              <h4
                className='font-bold mb-0'
                style={{
                  color: 'white',
                  marginBottom: '0px !important',
                  fontSize: '16px',
                }}
              >
                {f({id: 'CLAIM.OUTPATIENT.TOTAL_COVER'})}
              </h4>
              {/* <p className='text-3xl font-bold mb-4' style={{color: 'white'}}>Rp {totalBiaya.toLocaleString()}</p> */}
              <p className='text-3xl font-bold mb-4' style={{color: 'white'}}>
                {showAmount(billingSummary?.totalCoverNet)}
              </p>
            </div>
          </div>

          {/* The persisted read failed, so the net figures are unknown. Total Billing entry and
              Check Billing still work — Check Billing returns its own figures. */}
          {billingWorksheetFailed && !validateBillingData && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px',
                marginTop: '8px',
                padding: '8px',
                borderRadius: '8px',
                backgroundColor: '#fdf1f1',
                color: '#a12b2b',
                fontSize: '14px',
              }}
            >
              <span>{f({id: 'CLAIM.OUTPATIENT.BILLING_LOAD_FAILED'})}</span>
              <button
                type='button'
                onClick={fetchBillingWorksheet}
                style={{
                  backgroundColor: 'transparent',
                  border: '1px solid #a12b2b',
                  borderRadius: '5px',
                  color: '#a12b2b',
                  cursor: 'pointer',
                  padding: '4px 12px',
                }}
              >
                {f({id: 'CLAIM.OUTPATIENT.RETRY'})}
              </button>
            </div>
          )}

          {/* Plan adjustment ladder: gross cover, then the two components subtracted from it to
              reach the Total Cover above. Shown only when there is an adjustment to explain —
              with none, gross equals net and this row would just repeat the headline. It exists
              because the per-line Cover column stays gross, so without it the column visibly
              disagrees with the headline and nothing on screen says why. */}
          {billingSummary?.hasPlanAdjustment && (
            <div style={{display: 'flex', justifyContent: 'space-between', marginTop: '8px'}}>
              {[
                {
                  label: f({id: 'CLAIM.OUTPATIENT.GROSS_COVER'}),
                  value: billingSummary.grossCover,
                  last: false,
                },
                {
                  label: f({id: 'CLAIM.OUTPATIENT.DEDUCTIBLE_APPLIED'}),
                  value: billingSummary.deductionApplied,
                  last: false,
                },
                {
                  label: f({id: 'CLAIM.OUTPATIENT.COSHARE_APPLIED'}),
                  value: billingSummary.coShareApplied,
                  last: true,
                },
              ].map((card) => (
                <div
                  key={card.label}
                  style={{
                    backgroundColor: '#f1f1f4',
                    padding: '8px',
                    borderRadius: '8px',
                    color: '#252f4a',
                    textAlign: 'left',
                    flex: 1,
                    marginRight: card.last ? 0 : '8px',
                  }}
                >
                  <h4
                    className='font-bold mb-0'
                    style={{color: '#252f4a', marginBottom: '0px', fontSize: '14px'}}
                  >
                    {card.label}
                  </h4>
                  <p className='font-bold mb-0' style={{color: '#252f4a', fontSize: '18px'}}>
                    {showAmount(card.value)}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* The deduction was withheld pending manual verification, so the figures above can still
              change. Stated as consequence only — the reason is internal to the insurer. */}
          {billingSummary?.requiresReview && (
            <div
              style={{
                marginTop: '8px',
                padding: '8px',
                borderRadius: '8px',
                backgroundColor: '#fff8e6',
                color: '#7a5c00',
                fontSize: '14px',
              }}
            >
              {f({id: 'CLAIM.OUTPATIENT.REVIEW_PENDING'})}
            </div>
          )}

          {/* Scrollable costs section */}
          <div
            style={{
              overflowY: 'auto',
              flex: 1,
              paddingRight: '8px',
            }}
          >
            {uniqueSubBenefits.map((subBenefit: any, index: number) => {
              return (
                <div className='mb-4' key={subBenefit.subBenefitId}>
                  <div style={{display: 'flex', justifyContent: 'space-between'}}>
                    <label
                      className='form-label'
                      onClick={() => toggleExpand(index)}
                      style={{cursor: 'pointer'}}
                    >
                      {subBenefit.SubBenefitName}
                    </label>
                    <label
                      className='form-label'
                      onClick={() => toggleExpand(index)}
                      style={{cursor: 'pointer'}}
                    >
                      {expandedIndexes.includes(index) ? (
                        <FaChevronUp style={{marginRight: '5px'}} />
                      ) : (
                        <FaChevronDown style={{marginRight: '5px'}} />
                      )}
                    </label>
                  </div>
                  {expandedIndexes.includes(index) && (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <div style={{flex: 1, marginRight: '8px'}}>
                          <label className='form-label'>
                            Total <span style={{color: '#059BB6'}}> *</span>
                          </label>
                          <CurrencyFormat
                            className={`form-control ${errors['Total'] ? 'border-red-500' : ''}`}
                            value={costs[subBenefit.SubBenefitName] || 0}
                            onValueChange={(values) => {
                              if (isReadOnly) return
                              const {floatValue} = values
                              setCosts((prevCosts) => ({
                                ...prevCosts,
                                [subBenefit.SubBenefitName]: floatValue || 0,
                              }))
                              setIsBtnCheckBilling(false)
                              handleValidateBilling && handleValidateBilling(false)
                              // Discard the Check Billing result: it no longer describes these
                              // inputs, and leaving it on screen shows net totals that disagree
                              // with the visible lines.
                              setValidateBillingData(undefined)
                            }}
                            onKeyDown={(e) => {
                              // Convert comma to period for decimal input
                              if (e.key === ',') {
                                e.preventDefault()
                                const input = e.target as HTMLInputElement
                                const start = input.selectionStart || 0
                                const end = input.selectionEnd || 0
                                const value = input.value
                                const newValue = value.slice(0, start) + ',' + value.slice(end)
                                input.value = newValue
                                input.setSelectionRange(start + 1, start + 1)

                                // Trigger onChange event to update CurrencyFormat
                                const changeEvent = new Event('input', {bubbles: true})
                                input.dispatchEvent(changeEvent)
                              }
                            }}
                            // thousandSeparator={true}
                            thousandSeparator={'.'}
                            decimalSeparator={','}
                            decimalScale={2}
                            fixedDecimalScale={false}
                            allowNegative={false}
                            prefix={'Rp. '}
                            disabled={isReadOnly}
                            style={
                              isReadOnly
                                ? {
                                    backgroundColor: '#f5f5f5',
                                    cursor: 'not-allowed',
                                  }
                                : {}
                            }
                          />
                        </div>
                        <div style={{flex: 1, marginRight: '8px'}}>
                          <label className='form-label'>Exclusion</label>
                          <CurrencyFormat
                            className={`form-control ${errors['Excess'] ? 'border-red-500' : ''}`}
                            value={excess[subBenefit.SubBenefitName] || 0}
                            onValueChange={(values) => {
                              if (isReadOnly) return
                              const {floatValue} = values
                              setExcess((prevExcess) => ({
                                ...prevExcess,
                                [subBenefit.SubBenefitName]: floatValue || 0,
                              }))
                              setIsBtnCheckBilling(false)
                              handleValidateBilling && handleValidateBilling(false)
                              setValidateBillingData(undefined)
                            }}
                            onKeyDown={(e) => {
                              // Convert comma to period for decimal input
                              if (e.key === ',') {
                                e.preventDefault()
                                const input = e.target as HTMLInputElement
                                const start = input.selectionStart || 0
                                const end = input.selectionEnd || 0
                                const value = input.value
                                const newValue = value.slice(0, start) + ',' + value.slice(end)
                                input.value = newValue
                                input.setSelectionRange(start + 1, start + 1)

                                // Trigger onChange event to update CurrencyFormat
                                const changeEvent = new Event('input', {bubbles: true})
                                input.dispatchEvent(changeEvent)
                              }
                            }}
                            // thousandSeparator={true}
                            thousandSeparator={'.'}
                            decimalSeparator={','}
                            decimalScale={2}
                            fixedDecimalScale={false}
                            allowNegative={false}
                            prefix={'Rp. '}
                            disabled={isReadOnly}
                            style={
                              isReadOnly
                                ? {
                                    backgroundColor: '#f5f5f5',
                                    cursor: 'not-allowed',
                                  }
                                : {}
                            }
                          />
                        </div>
                      </div>
                      {/* Preview */}
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginTop: '10px',
                        }}
                      >
                        <div style={{flex: 1, marginRight: '8px'}}>
                          <label className='form-label'>
                            {f({id: 'CLAIM.OUTPATIENT.TOTAL_PREVIEW'})}
                          </label>
                          <input
                            type='text'
                            className={`form-control ${errors['Total'] ? 'border-red-500' : ''}`}
                            value={`Rp. ${formatNumberWithSeparator(
                              newTotal[subBenefit.SubBenefitName] || 0,
                              {minimumFractionDigits: 0, maximumFractionDigits: 2}
                            )}`}
                            disabled
                            style={{
                              backgroundColor: '#f5f5f5',
                              cursor: 'not-allowed',
                            }}
                          />
                        </div>
                        <div style={{flex: 1, marginRight: '8px'}}>
                          <label className='form-label'>
                            {f({id: 'CLAIM.OUTPATIENT.EXCLUSION_PREVIEW'})}
                          </label>
                          <input
                            type='text'
                            className={`form-control ${errors['Excess'] ? 'border-red-500' : ''}`}
                            value={`Rp. ${formatNumberWithSeparator(
                              newExcess[subBenefit.SubBenefitName] || 0,
                              {minimumFractionDigits: 0, maximumFractionDigits: 2}
                            )}`}
                            disabled
                            style={{
                              backgroundColor: '#f5f5f5',
                              cursor: 'not-allowed',
                            }}
                          />
                        </div>
                        <div style={{flex: 1}}>
                          <label className='form-label'>
                            {f({id: 'CLAIM.OUTPATIENT.REMARK_PREVIEW'})}
                          </label>
                          <Tooltip>
                            <input
                              type='text'
                              className={`form-control ${errors['Remark'] ? 'border-red-500' : ''}`}
                              value={newRemark[subBenefit.SubBenefitName] || ''}
                              disabled
                              style={{
                                backgroundColor: '#f5f5f5',
                                cursor: 'not-allowed',
                              }}
                            />
                            {newRemark[subBenefit.SubBenefitName] && (
                              <TooltipTextRemark className='tooltip-text'>
                                {newRemark[subBenefit.SubBenefitName]}
                              </TooltipTextRemark>
                            )}
                          </Tooltip>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )
            })}
          </div>

          {/* Optional: Add custom scrollbar styling */}
          <style>{`
                        /* Webkit browsers (Chrome, Safari) */
                        div::-webkit-scrollbar {
                            width: 8px;
                        }
                        
                        div::-webkit-scrollbar-track {
                            background: #f1f1f1;
                            border-radius: 4px;
                        }
                        
                        div::-webkit-scrollbar-thumb {
                            background: #888;
                            border-radius: 4px;
                        }
                        
                        div::-webkit-scrollbar-thumb:hover {
                            background: #555;
                        }
                        
                        /* Firefox */
                        div {
                            scrollbar-width: thin;
                            scrollbar-color: #888 #f1f1f1;
                        }
                    `}</style>
        </div>
        <style>{`
                    .border-red-500 {
                        border: 1px solid red !important;
                    }
                `}</style>
      </div>
      {/* <PreviewPDFModal
        isOpen={showPreview}
        onClose={() => {
          setShowPreview(false)
          navigate('/claims')
        }}
        data={{
          diagnosis,
          diagnosis2: selectedSupportingIcds[0]
            ? `${selectedSupportingIcds[0].name} (${selectedSupportingIcds[0].code})`
            : '',
          diagnosis3: selectedSupportingIcds[1]
            ? `${selectedSupportingIcds[1].name} (${selectedSupportingIcds[1].code})`
            : '',
          diagnosis4: selectedSupportingIcds[2]
            ? `${selectedSupportingIcds[2].name} (${selectedSupportingIcds[2].code})`
            : '',
          doctor,
          prescription,
          department,
          notes,
          specialTerms,
          costs,
          totalBiaya,
          claimDetails,
          dischargeBillData,
        }}
      /> */}
      {/* FGL Document Modal */}
      <Modal
        show={showPreview}
        onHide={() => {
          setShowPreview(false)
          navigate('/claims')
        }}
        size='xl'
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{f({id: 'CLAIM.OUTPATIENT.FGL_DOCUMENT_PREVIEW'})}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{padding: 0, height: 'calc(90vh - 120px)'}}>
          {isLoadingPreview && (
            <div className='loading-container'>
              <div className='loading-spinner'></div>
              <div className='loading-text'>{f({id: 'CLAIM.OUTPATIENT.MODAL_LOADING'})}...</div>
            </div>
          )}
          {!isLoadingPreview && fglDocumentUrl && (
            <iframe
              src={`https://docs.google.com/viewer?url=${encodeURIComponent(
                fglDocumentUrl
              )}&embedded=true`}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              title='FGL Dokumen Preview'
              frameBorder='0'
              onLoad={() => setIsLoadingPreview(false)}
            />
          )}
          {fglDocumentUrl && isLoadingPreview && (
            <iframe
              src={`https://docs.google.com/viewer?url=${encodeURIComponent(
                fglDocumentUrl
              )}&embedded=true`}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                display: 'none', // Hide while loading
              }}
              title='FGL Dokumen Preloader'
              frameBorder='0'
              onLoad={() => setIsLoadingPreview(false)}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* Document Modal */}
      <Modal show={showDocModal} onHide={() => setShowDocModal(false)} size='xl' centered>
        <Modal.Header closeButton>
          <Modal.Title>{f({id: 'CLAIM.OUTPATIENT.REGISTRATION_DOCUMENT'})}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{padding: 0, height: 'calc(90vh - 120px)'}}>
          <iframe
            src={`https://docs.google.com/viewer?url=${encodeURIComponent(
              claimDetails?.urlRegisterForm
            )}&embedded=true`}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
            }}
            title='Register Form Preview'
            frameBorder='0'
          />
        </Modal.Body>
      </Modal>

      {/* // Modal for Change Benefit */}
      <Modal show={showChangeBenefitModal} onHide={handleCancelChanges}>
        <Modal.Header closeButton>
          <Modal.Title>{f({id: 'CLAIM.OUTPATIENT.CHANGE_BENEFIT'})}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='mb-3'>
            <label className='form-label'>{f({id: 'CLAIM.OUTPATIENT.BENEFIT'})}</label>
            <select
              className='form-control'
              value={selectedBenefit}
              onChange={(e) => handleBenefitChange(e.target.value)}
            >
              <option value=''>{f({id: 'CLAIM.OUTPATIENT.SELECT_BENEFIT'})}</option>
              {benefit
                ?.filter((plan: any) => {
                  // If treatment type is Rawat Inap, only show specific benefits
                  if (claimDetails?.claim?.treatmentType?.toLowerCase() === 'rawat inap') {
                    return ['INPATIENT', 'INPATIENT-OUTPATIENT-KHUSUS', 'MATERNITY'].includes(
                      plan.benefitName.toUpperCase()
                    )
                  }
                  // For Rawat Jalan or any other type, show all benefits
                  return true
                })
                .map((plan: any) => (
                  <option key={plan.id} value={plan.benefitName}>
                    {plan.benefitName}
                  </option>
                ))}
            </select>
          </div>
          <div className='mb-3'>
            <label className='form-label'>{f({id: 'CLAIM.OUTPATIENT.PLAN'})}</label>
            <input
              type='text'
              className='form-control'
              value={selectedPlan}
              // onChange={(e) => setDisabledInputValue(e.target.value)}
              disabled
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={handleCancelChanges}>
            {f({id: 'CLAIM.OUTPATIENT.CANCEL'})}
          </Button>
          <Button variant='primary' disabled={!isChanged} onClick={handleSaveChangeBenefit}>
            {f({id: 'CLAIM.OUTPATIENT.SAVE'})}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add document preview modal */}
      <Modal
        show={showDocumentPreviewModal}
        onHide={() => {
          setShowDocumentPreviewModal(false)
          setPreviewDocumentUrl('')
          setIsDocumentLoading(false)
        }}
        size='xl'
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {f({id: 'CLAIM.OUTPATIENT.DOCUMENT_PREVIEW'})}: {previewDocumentName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{padding: 0, height: 'calc(90vh - 120px)', position: 'relative'}}>
          {isDocumentLoading && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                zIndex: 10,
              }}
            >
              <Spinner animation='border' role='status' style={{color: '#059BB6'}}>
                <span className='visually-hidden'>Loading...</span>
              </Spinner>
              <span style={{marginLeft: '10px', color: '#059BB6', fontWeight: 500}}>
                Loading document...
              </span>
            </div>
          )}
          {previewDocumentUrl && (
            <iframe
              src={`https://docs.google.com/viewer?url=${encodeURIComponent(
                previewDocumentUrl
              )}&embedded=true`}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              title='Document Preview'
              frameBorder='0'
              onLoad={() => {
                // Give it a moment to render properly
                setTimeout(() => {
                  setIsDocumentLoading(false)
                }, 1000)
              }}
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  )
})

export default OutpatientPage
